import React, { useState, useEffect, useCallback } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { Star, Pin, PinOff, MessageSquare, Trash2, Loader2, ChevronLeft, ChevronRight, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL as API } from '@/utils/apiConfig';

function Stars({ value, size = 'sm' }) {
  const sz = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} className={sz} fill={i <= value ? '#f59e0b' : 'transparent'} stroke={i <= value ? '#f59e0b' : 'currentColor'} style={{ color: 'var(--theme-textSecondary, #888)' }} />
      ))}
    </div>
  );
}

export default function AdminReviewsPage() {
  const { isDark, colors } = useAdminTheme();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [pinnedFilter, setPinnedFilter] = useState('all');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  const perPage = 30;

  const fetchReviews = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: perPage });
      if (ratingFilter !== 'all') params.set('rating', ratingFilter);
      if (pinnedFilter !== 'all') params.set('pinned', pinnedFilter);
      const res = await axios.get(`${API}/admin/reviews?${params}`, { withCredentials: true });
      setReviews(res.data.reviews);
      setTotal(res.data.total);
      setAvgRating(res.data.avg_rating);
      setTotalCount(res.data.total_count);
      setPage(p);
    } catch { toast.error('Failed to load reviews'); }
    setLoading(false);
  }, [ratingFilter, pinnedFilter]);

  useEffect(() => { fetchReviews(1); }, [fetchReviews]);

  const handlePin = async (reviewId) => {
    try {
      const res = await axios.put(`${API}/admin/reviews/${reviewId}/pin`, {}, { withCredentials: true });
      toast.success(res.data.pinned ? 'Review pinned' : 'Review unpinned');
      fetchReviews(page);
    } catch { toast.error('Failed to update'); }
  };

  const handleReply = async (reviewId) => {
    if (!replyText.trim()) { toast.error('Reply cannot be empty'); return; }
    setReplyLoading(true);
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}/reply`, { reply: replyText.trim() }, { withCredentials: true });
      toast.success('Reply posted');
      setReplyingTo(null);
      setReplyText('');
      fetchReviews(page);
    } catch { toast.error('Failed to post reply'); }
    setReplyLoading(false);
  };

  const handleDeleteReply = async (reviewId) => {
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}/reply`, { withCredentials: true });
      toast.success('Reply removed');
      fetchReviews(page);
    } catch { toast.error('Failed to remove reply'); }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { withCredentials: true });
      toast.success('Review deleted');
      fetchReviews(page);
    } catch { toast.error('Failed to delete'); }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto" data-testid="admin-reviews-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>Community Reviews</h1>
            {totalCount > 0 && (
              <div className="flex items-center gap-2 mt-1">
                <Stars value={Math.round(avgRating)} />
                <span className="text-sm font-medium" style={{ color: colors.accent }}>{avgRating}</span>
                <span className="text-xs" style={{ color: colors.textMuted }}>({totalCount} reviews)</span>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={ratingFilter}
              onChange={e => setRatingFilter(e.target.value)}
              className="text-xs rounded-lg px-3 py-2 border"
              style={{ background: colors.card, color: colors.text, borderColor: colors.border }}
              data-testid="rating-filter"
            >
              <option value="all">All Ratings</option>
              {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Star{r > 1 && 's'}</option>)}
            </select>
            <select
              value={pinnedFilter}
              onChange={e => setPinnedFilter(e.target.value)}
              className="text-xs rounded-lg px-3 py-2 border"
              style={{ background: colors.card, color: colors.text, borderColor: colors.border }}
              data-testid="pinned-filter"
            >
              <option value="all">All</option>
              <option value="true">Pinned</option>
              <option value="false">Unpinned</option>
            </select>
          </div>
        </div>

        {/* Reviews */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.accent }} />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-16 rounded-xl" style={{ background: colors.card, border: `1px solid ${colors.border}` }}>
            <MessageSquare className="h-10 w-10 mx-auto mb-2" style={{ color: colors.textMuted, opacity: 0.4 }} />
            <p className="text-sm" style={{ color: colors.textMuted }}>No reviews found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.map(review => {
              const isPinned = review.pinned === true;
              const date = new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

              return (
                <div
                  key={review.review_id}
                  className="rounded-xl p-4 transition-all"
                  style={{
                    background: colors.card,
                    border: `1px solid ${isPinned ? colors.accent + '40' : colors.border}`,
                  }}
                  data-testid={`admin-review-${review.review_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-xs font-bold"
                        style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', color: colors.accent }}
                      >
                        {(review.display_name || 'A')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold truncate" style={{ color: colors.text }}>
                            {review.display_name}
                          </span>
                          {isPinned && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: colors.accent + '20', color: colors.accent }}>
                              Pinned
                            </span>
                          )}
                          <span className="text-xs shrink-0" style={{ color: colors.textMuted }}>{date}</span>
                        </div>
                        <Stars value={review.rating} />
                        <p className="text-sm mt-1.5 leading-relaxed" style={{ color: colors.textSecondary }}>
                          {review.comment}
                        </p>
                        <p className="text-[11px] mt-1" style={{ color: colors.textMuted }}>
                          User: {review.user_id}
                        </p>

                        {/* Admin Reply Display */}
                        {review.admin_reply && (
                          <div className="mt-3 rounded-lg p-3" style={{ background: isDark ? 'rgba(99,102,241,0.08)' : 'rgba(79,70,229,0.06)', border: `1px solid ${colors.accent}20` }}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold" style={{ color: colors.accent }}>Eili Team</span>
                              <button onClick={() => handleDeleteReply(review.review_id)} className="text-xs hover:opacity-70" style={{ color: colors.textMuted }} data-testid={`delete-reply-${review.review_id}`}>
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <p className="text-sm" style={{ color: colors.textSecondary }}>{review.admin_reply}</p>
                          </div>
                        )}

                        {/* Reply Form */}
                        {replyingTo === review.review_id && (
                          <div className="mt-3">
                            <Textarea
                              value={replyText}
                              onChange={e => setReplyText(e.target.value)}
                              placeholder="Write a reply as Eili Team..."
                              rows={3}
                              maxLength={500}
                              className="text-sm rounded-lg border resize-none mb-2"
                              style={{ background: colors.card, color: colors.text, borderColor: colors.border }}
                              data-testid={`reply-input-${review.review_id}`}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleReply(review.review_id)}
                                disabled={replyLoading}
                                className="gap-1.5 text-xs rounded-lg text-white"
                                style={{ background: colors.accent }}
                                data-testid={`submit-reply-${review.review_id}`}
                              >
                                {replyLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                                Post Reply
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setReplyingTo(null); setReplyText(''); }} className="text-xs rounded-lg" style={{ color: colors.textMuted }}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-1 shrink-0">
                      <button
                        onClick={() => handlePin(review.review_id)}
                        className="p-1.5 rounded-lg transition-colors hover:opacity-80"
                        style={{ background: isPinned ? colors.accent + '20' : 'transparent', color: isPinned ? colors.accent : colors.textMuted }}
                        title={isPinned ? 'Unpin' : 'Pin to landing page'}
                        data-testid={`pin-btn-${review.review_id}`}
                      >
                        {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => { setReplyingTo(review.review_id); setReplyText(review.admin_reply || ''); }}
                        className="p-1.5 rounded-lg transition-colors hover:opacity-80"
                        style={{ color: review.admin_reply ? colors.accent : colors.textMuted }}
                        title={review.admin_reply ? 'Edit reply' : 'Reply'}
                        data-testid={`reply-btn-${review.review_id}`}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(review.review_id)}
                        className="p-1.5 rounded-lg transition-colors hover:opacity-80"
                        style={{ color: '#ef4444' }}
                        title="Delete review"
                        data-testid={`delete-btn-${review.review_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <Button
              size="sm" variant="ghost" disabled={page <= 1}
              onClick={() => fetchReviews(page - 1)}
              style={{ color: colors.textMuted }}
              data-testid="reviews-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs" style={{ color: colors.textMuted }}>Page {page} of {totalPages}</span>
            <Button
              size="sm" variant="ghost" disabled={page >= totalPages}
              onClick={() => fetchReviews(page + 1)}
              style={{ color: colors.textMuted }}
              data-testid="reviews-next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
